import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUser } from './UserContext';
import { csrfFetch } from '../utils/csrfFetch';

const FolderContext = createContext(null);

const API_BASE = '/api';

/**
 * FolderProvider - Manages folder state and operations
 */
export function FolderProvider({ children }) {
  const { user } = useUser();
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Fetch all folders
  const fetchFolders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await csrfFetch(`${API_BASE}/folders`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      } else {
        throw new Error('Failed to fetch folders');
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError(err.message);
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load folders on mount — guard behind auth to avoid pre-auth 403 errors
  useEffect(() => {
    if (user) fetchFolders();
  }, [fetchFolders, user]);

  // Create a new folder
  const createFolder = useCallback(async (name, parentFolderId = null) => {
    try {
      const response = await csrfFetch(`${API_BASE}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, parentFolderId })
      });

      if (response.ok) {
        const newFolder = await response.json();
        await fetchFolders(); // Refresh to get proper tree structure
        return newFolder;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create folder');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      throw err;
    }
  }, [fetchFolders]);

  // Rename a folder
  const renameFolder = useCallback(async (folderId, name) => {
    try {
      const response = await csrfFetch(`${API_BASE}/folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        const updatedFolder = await response.json();
        // Update local state
        setFolders(prev => updateFolderInTree(prev, folderId, { name }));
        return updatedFolder;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to rename folder');
      }
    } catch (err) {
      console.error('Error renaming folder:', err);
      throw err;
    }
  }, []);

  // Delete a folder
  const deleteFolder = useCallback(async (folderId) => {
    try {
      const response = await csrfFetch(`${API_BASE}/folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchFolders(); // Refresh tree
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete folder');
      }
    } catch (err) {
      console.error('Error deleting folder:', err);
      throw err;
    }
  }, [fetchFolders]);

  // Add conversation to folder
  const addConversationToFolder = useCallback(async (folderId, conversationId) => {
    try {
      const response = await csrfFetch(`${API_BASE}/folders/${folderId}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversationId })
      });

      if (response.ok) {
        await fetchFolders(); // Refresh to update conversation counts
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add conversation to folder');
      }
    } catch (err) {
      console.error('Error adding conversation to folder:', err);
      throw err;
    }
  }, [fetchFolders]);

  // Remove conversation from folder
  const removeConversationFromFolder = useCallback(async (folderId, conversationId) => {
    try {
      const response = await csrfFetch(`${API_BASE}/folders/${folderId}/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchFolders(); // Refresh
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove conversation from folder');
      }
    } catch (err) {
      console.error('Error removing conversation from folder:', err);
      throw err;
    }
  }, [fetchFolders]);

  // Toggle folder expanded state
  const toggleFolderExpanded = useCallback((folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  }, []);

  // Check if a folder is expanded
  const isFolderExpanded = useCallback((folderId) => {
    return expandedFolders[folderId] !== false; // Default to expanded
  }, [expandedFolders]);

  // Get all conversation IDs that are in folders
  const getFolderedConversationIds = useCallback(() => {
    const ids = new Set();

    const collectIds = (folderList) => {
      folderList.forEach(folder => {
        if (folder.conversations) {
          folder.conversations.forEach(conv => ids.add(conv.id));
        }
        if (folder.children) {
          collectIds(folder.children);
        }
      });
    };

    collectIds(folders);
    return ids;
  }, [folders]);

  // Find which folder a conversation is in
  const findConversationFolder = useCallback((conversationId) => {
    const searchFolder = (folderList) => {
      for (const folder of folderList) {
        if (folder.conversations?.some(c => c.id === conversationId)) {
          return folder;
        }
        if (folder.children) {
          const found = searchFolder(folder.children);
          if (found) return found;
        }
      }
      return null;
    };

    return searchFolder(folders);
  }, [folders]);

  const value = {
    folders,
    isLoading,
    error,
    expandedFolders,
    fetchFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    addConversationToFolder,
    removeConversationFromFolder,
    toggleFolderExpanded,
    isFolderExpanded,
    getFolderedConversationIds,
    findConversationFolder
  };

  return (
    <FolderContext.Provider value={value}>
      {children}
    </FolderContext.Provider>
  );
}

// Hook to use folder context
export function useFolder() {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolder must be used within a FolderProvider');
  }
  return context;
}

// Helper function to update a folder in the tree
function updateFolderInTree(folders, folderId, updates) {
  return folders.map(folder => {
    if (folder.id === folderId) {
      return { ...folder, ...updates };
    }
    if (folder.children) {
      return {
        ...folder,
        children: updateFolderInTree(folder.children, folderId, updates)
      };
    }
    return folder;
  });
}

export default FolderContext;
